import { AxisDateTimeFull } from "../util/AxisDateTime";
import { getStatusBadge } from "../util/RefereeStatusUtils";
import Table from "react-bootstrap/Table";
import React, { Fragment, useState, useEffect } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { ShiftTableTitle } from "./ShiftTableTitle";
import { useAuth } from "../context/AuthContext";
import { BsSend, BsArrowRepeat, BsArrowClockwise, BsFileEarmarkPdf, BsX, BsEye, BsPencilSquare, BsTelephoneFill } from "react-icons/bs";
import AxisSpinner from "./AxisSpinner";
import { saveAs } from "file-saver";
import ResetEmailModal from "../modals/ResetEmailModal";
import UploadPdfModal from "../modals/UploadPdfModal";
import CancelRefereeModal from "../modals/CancelRefereeModal";
import EditRefereeModal from "../modals/EditRefereeModal";
import ViewPdfModal from "../modals/ViewPdfModal";
import { UserLink } from "../util/Links";

const RefereeDetailsElement = ({ refereeId, user, onRefereeUpdate }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [referee, setReferee] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (refereeId && user?.id) {
      fetchReferee();
    }
  }, [refereeId, user?.id]);


  const fetchReferee = async () => {
    if (!refereeId || !user?.id) return;
    
    try {
      setFetchLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { 
          user_id: user.id,
          referee_id: refereeId 
        },
      };

      const response = await authAxios().get(
        "/api/backoffice/users/references/get",
        config
      );

      setReferee(response.data);
      setFetchLoading(false);
    } catch (error) {
      setFetchLoading(false);
      console.log("Error fetching referee:", error);
    }
  };
  
  if (fetchLoading) {
    return (
      <Fragment>
        <ShiftTableTitle title={"Reference Detail"} />
        <div className="text-center py-4">
          <AxisSpinner loading={fetchLoading} />
        </div>
      </Fragment>
    );
  }

  if (referee === null || referee === undefined) return null;

  const handleSendReminder = async () => {
    if (!window.confirm(`Send reminder to ${referee.refereeName} at ${referee.refereeEmail}?`)) {
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { 
          user_id: user.id,
          referee_id: referee.id,
          message: "Please complete your reference as soon as possible."
        },
      };

      await authAxios().post("/api/backoffice/users/references/remind", null, config);
      
      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error sending reminder:", error);
      alert("Failed to send reminder. Please try again.");
    }
  };


  const canSendReminder = (status) => {
    return status === "sent" || status === "delivered";
  };

  const canSend = (status) => {
    return status === "created" || status === "rejected";
  };

  const canReset = (status) => {
    return status === "sent" || status === "delivered";
  };

  const canUpload = (status) => {
    return status === "created" || status === "sent";
  };

  const canCancel = (status) => {
    return status === "sent" || status === "delivered";
  };

  const canEdit = (status) => {
    return status === "created" || status === "rejected";
  };

  const canRefresh = (status) => {
    return status === "sent";
  };

  const handleRefresh = async () => {
    if (!window.confirm(`Refresh status for ${referee.refereeName}? This will check LegalSign for updates.`)) {
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: {
          user_id: user.id,
          referee_id: referee.id
        },
      };

      await authAxios().post("/api/backoffice/users/references/refresh", null, config);

      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error refreshing referee:", error);
      alert("Failed to refresh referee status. Please try again.");
    }
  };

  const handleUpdateReferee = async (name, email) => {
    if (!name.trim() || !email.trim()) {
      alert("Please enter both name and email.");
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        id: referee.id,
        userId: user.id,
        refereeName: name.trim(),
        refereeEmail: email.trim()
      };

      await authAxios().post("/api/backoffice/users/references/update", requestData);
      
      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }
      
      setShowEditModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error updating referee:", error);
      alert("Failed to update referee. Please try again.");
    }
  };

  const canViewPdf = (pdfStorage) => {
    return pdfStorage && pdfStorage.trim() !== "";
  };

  const handleViewPdf = async () => {
    if (!referee?.pdfStorage) return;
    
    try {
      setPdfLoading(true);
      
      const config = {
        responseType: "arraybuffer",
        params: {
          user_id: user.id,
          referee_id: refereeId
        },
      };

      const response = await authAxios().get(
        "/api/backoffice/users/referee_file",
        config,
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = window.URL.createObjectURL(blob);
      setPdfUrl(pdfUrl);
      setShowPdfModal(true);
      
      setPdfLoading(false);
    } catch (error) {
      setPdfLoading(false);
      console.log("Error fetching PDF:", error);
      alert("Failed to load PDF. Please try again.");
    }
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const filename = `reference-${referee?.refereeName || 'unknown'}-${referee?.id}.pdf`;
      saveAs(pdfUrl, filename);
    }
  };

  const handleCancelReferee = async (reason) => {
    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { 
          user_id: user.id,
          referee_id: referee.id,
          reason: reason?.trim() || "Cancelled by admin"
        },
      };

      await authAxios().post("/api/backoffice/users/references/cancel", null, config);
      
      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }
      
      setShowCancelModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error cancelling referee:", error);
      alert("Failed to cancel referee. Please try again.");
    }
  };

  const handleUploadPdf = async (uploadFile) => {
    if (!uploadFile) {
      alert("Please select a valid PDF file.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("user_id", user.id);
      formData.append("referee_id", refereeId);

      await authAxios().post("/api/backoffice/users/references/upload", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }
      
      setShowUploadModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error uploading PDF:", error);
      alert("Failed to upload PDF. Please try again.");
    }
  };

  const handleResetEmail = async (email) => {
    if (!email || !email.trim()) {
      alert("Please enter an email address.");
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: { 
          user_id: user.id,
          referee_id: referee.id,
          email: email.trim(),
          notify: true
        },
      };

      await authAxios().post("/api/backoffice/users/references/reset", null, config);
      
      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }
      
      setShowResetModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error resetting email:", error);
      alert("Failed to reset email. Please try again.");
    }
  };

  const handleSendRefereeRequest = async () => {
    if (!window.confirm(`Send referee request to ${referee.refereeName} at ${referee.refereeEmail}?`)) {
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: {
          user_id: user.id,
          referee_id: referee.id
        },
      };

      await authAxios().post("/api/backoffice/users/references/send", null, config);

      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error sending referee request:", error);
      alert("Failed to send referee request. Please try again.");
    }
  };

  const handleMarkContacted = async () => {
    if (!window.confirm(`Mark referee ${referee.refereeName} as contacted?`)) {
      return;
    }

    try {
      setLoading(true);
      const config = {
        headers: { "content-type": "application/json" },
        params: {
          user_id: user.id,
          referee_id: referee.id
        },
      };

      await authAxios().post("/api/backoffice/users/references/mark_contacted", null, config);

      // Refresh local data and call parent callback
      await fetchReferee();
      if (onRefereeUpdate) {
        onRefereeUpdate();
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error marking referee as contacted:", error);
      alert("Failed to mark referee as contacted. Please try again.");
    }
  };

  return (
    <Fragment>
      <ShiftTableTitle title={"Reference Detail"} />
      <Table striped hover responsive>
        <tbody>
          <tr>
            <td className={"tdw-50"}>Name</td>
            <td className={"font-weight-bold"}>{referee?.refereeName}</td>
          </tr>
          <tr>
            <td>Email</td>
            <td className={"font-weight-bold"}>{referee?.refereeEmail}</td>
          </tr>
          <tr>
            <td>Status</td>
            <td>{getStatusBadge(referee?.status)}</td>
          </tr>
          <tr>
            <td>Contacted</td>
            <td>
              {referee?.refereeContacted ? (
                <Badge bg="success">Yes</Badge>
              ) : (
                <Badge bg="secondary">No</Badge>
              )}
            </td>
          </tr>
          {referee?.refereeContactedByUser && (
            <tr>
              <td>Contacted By</td>
              <td>
                {UserLink(referee.refereeContactedByUser)}
              </td>
            </tr>
          )}
          <tr>
            <td>Last Event</td>
            <td className={"font-weight-bold"}>{referee?.lastEvent || 'N/A'}</td>
          </tr>
          {referee?.lastEventAt && (
            <tr>
              <td>Last Event Date</td>
              <td className={"font-weight-bold"}>
                {AxisDateTimeFull(referee?.lastEventAt)}
              </td>
            </tr>
          )}
          <tr>
            <td>Created</td>
            <td className={"font-weight-bold"}>
              {AxisDateTimeFull(referee?.created)}
            </td>
          </tr>
          {referee?.documentUuid && (
            <tr>
              <td>Document UUID</td>
              <td className={"font-weight-bold font-monospace small"}>
                {referee?.documentUuid}
              </td>
            </tr>
          )}
          {referee?.signerUuid && (
            <tr>
              <td>Signer UUID</td>
              <td className={"font-weight-bold font-monospace small"}>
                {referee?.signerUuid}
              </td>
            </tr>
          )}
          {referee?.templateUri && (
            <tr>
              <td>Template URI</td>
              <td className={"font-weight-bold small"}>{referee?.templateUri}</td>
            </tr>
          )}
          {referee?.groupUri && (
            <tr>
              <td>Group URI</td>
              <td className={"font-weight-bold small"}>{referee?.groupUri}</td>
            </tr>
          )}
          {referee?.pdfStorage && (
            <tr>
              <td>PDF Storage</td>
              <td className={"font-weight-bold"}>{referee?.pdfStorage}</td>
            </tr>
          )}
          {referee?.rejectionReason && (
            <tr>
              <td>Rejection Reason</td>
              <td className={"font-weight-bold text-danger"}>
                {referee?.rejectionReason}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      
      <div className={"shift-button-container mt-3"}>
        <Container>
          <Row xs={"auto"} className={"justify-content-lg-center shift-button"}>
            {canSend(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-approve icon-text-wrapper"}
                  onClick={handleSendRefereeRequest}
                  disabled={loading}
                >
                  <BsSend size={19} className="icon-right-spacing" />
                  {loading ? "Sending Request..." : "Send Reference Request"}
                </Button>
              </Col>
            )}

            {canSendReminder(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-pending icon-text-wrapper"}
                  onClick={handleSendReminder}
                  disabled={loading}
                >
                  <BsSend size={19} className="icon-right-spacing" />
                  {loading ? "Sending Reminder..." : "Send Reminder"}
                </Button>
              </Col>
            )}

            {canRefresh(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-approve icon-text-wrapper"}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <BsArrowClockwise size={19} className="icon-right-spacing" />
                  {loading ? "Refreshing..." : "Refresh Status"}
                </Button>
              </Col>
            )}

            {canReset(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-reassign icon-text-wrapper"}
                  onClick={() => setShowResetModal(true)}
                  disabled={loading}
                >
                  <BsArrowRepeat size={19} className="icon-right-spacing" />
                  Reset Email
                </Button>
              </Col>
            )}

            {canEdit(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-edit icon-text-wrapper"}
                  onClick={() => setShowEditModal(true)}
                  disabled={loading}
                >
                  <BsPencilSquare size={19} className="icon-right-spacing" />
                  Edit Details
                </Button>
              </Col>
            )}

            {canUpload(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-new-shift icon-text-wrapper"}
                  onClick={() => setShowUploadModal(true)}
                  disabled={loading}
                >
                  <BsFileEarmarkPdf size={19} className="icon-right-spacing" />
                  Upload PDF
                </Button>
              </Col>
            )}

            {canViewPdf(referee?.pdfStorage) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-edit icon-text-wrapper"}
                  onClick={handleViewPdf}
                  disabled={loading || pdfLoading}
                >
                  <BsEye size={19} className="icon-right-spacing" />
                  {pdfLoading ? "Loading PDF..." : "View PDF"}
                </Button>
              </Col>
            )}

            {canCancel(referee?.status) && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-cancel icon-text-wrapper"}
                  onClick={() => setShowCancelModal(true)}
                  disabled={loading}
                >
                  <BsX size={19} className="icon-right-spacing" />
                  Cancel Reference
                </Button>
              </Col>
            )}

            {!referee?.refereeContacted && (
              <Col className={"g-3"}>
                <Button
                  className={"shift-button-approve icon-text-wrapper"}
                  onClick={handleMarkContacted}
                  disabled={loading}
                >
                  <BsTelephoneFill size={19} className="icon-right-spacing" />
                  {loading ? "Marking..." : "Mark as Contacted"}
                </Button>
              </Col>
            )}
          </Row>
        </Container>
      </div>

      <ResetEmailModal
        visible={showResetModal}
        close={() => setShowResetModal(false)}
        referee={referee}
        user={user}
        onReset={handleResetEmail}
        loading={loading}
      />

      <UploadPdfModal
        visible={showUploadModal}
        close={() => setShowUploadModal(false)}
        onUpload={handleUploadPdf}
        loading={loading}
      />

      <CancelRefereeModal
        visible={showCancelModal}
        close={() => setShowCancelModal(false)}
        onCancel={handleCancelReferee}
        loading={loading}
      />

      <EditRefereeModal
        visible={showEditModal}
        close={() => setShowEditModal(false)}
        referee={referee}
        onUpdate={handleUpdateReferee}
        loading={loading}
      />

      <ViewPdfModal
        visible={showPdfModal}
        close={() => setShowPdfModal(false)}
        pdfUrl={pdfUrl}
        title={referee?.pdfStorage}
        onDownload={handleDownloadPdf}
        loading={pdfLoading}
      />
    </Fragment>
  );
};

export default RefereeDetailsElement;